import { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Which set of routes the sidebar is showing. `employee` is the resting state —
 * everyone's own pages. `hr` swaps the sidebar over to the HR pages instead;
 * it is a quick-action view, not a change of what the account may do.
 */
export type NavView = 'employee' | 'hr';

const NAV_VIEW_KEY = 'andhub:navView';

interface SidebarCtx {
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (v: boolean) => void;
  /** Which route set the sidebar shows. */
  navView: NavView;
  setNavView: (v: NavView) => void;
}

const SidebarContext = createContext<SidebarCtx>({
  collapsed: false,
  toggle: () => {},
  setCollapsed: () => {},
  navView: 'employee',
  setNavView: () => {},
});

export function SidebarProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  // Remembered per browser so a reload doesn't drop someone back to their own
  // routes mid-task. An 'admin' left over from the old two-pill switch reads as
  // the resting state.
  const [navView, setNavViewState] = useState<NavView>(() => {
    try {
      return localStorage.getItem(NAV_VIEW_KEY) === 'hr' ? 'hr' : 'employee';
    } catch {
      return 'employee';
    }
  });

  const setNavView = useCallback((v: NavView) => setNavViewState(v), []);

  useEffect(() => {
    try {
      localStorage.setItem(NAV_VIEW_KEY, navView);
    } catch {
      // Private browsing or blocked storage — the view just won't persist.
    }
  }, [navView]);

  return (
    <SidebarContext.Provider
      value={{ collapsed, toggle: () => setCollapsed(c => !c), setCollapsed, navView, setNavView }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarCtx {
  return useContext(SidebarContext);
}
